import { ValidationErrors } from '@angular/forms';
import { Observable } from 'rxjs';

export enum ImportStatus {
	SuccessCreate = 'successCreate',
	SuccessUpdate = 'successUpdate',
	Failed = 'failed',
	Skipped = 'skipped',
	ValidationError = 'validationError',
	ParsingError = 'parsingError',
}

export interface ImportResponse {
	successfulImports: SuccessFullImport[];
	errors: ImportErrorTypes[];
}

export type ImportErrorTypes = SkippedImport | FailedImport | FailedParse | FailedValidationImport;

export interface SuccessFullImport {
	Id: string;
	status: ImportStatus.SuccessCreate | ImportStatus.SuccessUpdate;
}

export interface FailedImport {
	Id: string;
	/** Where the row came from, worded by whichever format read it. Preferred over `Id` when reporting, since a file position is what the user can act on; absent when the transport has no notion of position, and `Id` is the fallback. */
	location?: string;
	status: ImportStatus.Failed;
	message: string;
}

export interface SkippedImport {
	Id: string;
	/** Where the row came from, worded by whichever format read it. Preferred over `Id` when reporting, since a file position is what the user can act on; absent when the transport has no notion of position, and `Id` is the fallback. */
	location?: string;
	status: ImportStatus.Skipped;
	message: string;
}

export interface FailedValidationImport {
	Id: string;
	/** Where the row came from, worded by whichever format read it. Preferred over `Id` when reporting, since a file position is what the user can act on; absent when the transport has no notion of position, and `Id` is the fallback. */
	location?: string;
	status: ImportStatus.ValidationError;
	errors: Record<string, ValidationErrors>;
}

/** A file that could not be parsed. There is no entity to key on, so `message` carries the whole address — including the row number for row-oriented formats, which the reading transport folds in because only it knows whether rows exist. This contract is shared with JSON imports, where a row number is meaningless. */
export interface FailedParse {
	status: ImportStatus.ParsingError;
	message: string;
}

export interface ITransferService<T> {
	exportData: (user: T[]) => Observable<T[]>;
	importData: (file: File | File[], overwrite?: boolean) => Observable<ImportResponse>;
	/**
	 * Does this entity offer a downloadable import template? Lets a component render a "Download template" affordance generically, without knowing the transport.
	 *
	 * Required rather than optional deliberately: an optional member compiles when forgotten, so the next entity to gain a template would silently ship without wiring it.
	 */
	hasTemplate: () => boolean;
	/**
	 * Pure delegation — forwards to the file service with this entity's own schema and filename. Contains no template-building logic, so the schema stays the single writer of header text, order, comments, and widths.
	 *
	 * Implementations that answer `hasTemplate() === false` must throw, so a UI that ignores `hasTemplate()` fails loudly instead of appearing to work.
	 */
	getTemplate: () => void;
}
